import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SimpleChartData } from '@app/app/shared/components/carts/SimpleChartData';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { FeedbackEnrichedAnswer, FeedbackRecipient, IncludedFeedbackQuestion, Organization } from '@app/app/shared/models';

interface Marker {
  uid?: string;
  label: string;
  image?: string;
  score: number;
  leftPercent: number;
}

interface BucketModel {
  value: number;
  leftPercent: number;
  markers: Marker[];
  sizeClass: 'large' | 'medium' | 'small';
  visibleCount: number;
}

@Component({
  selector: 'ptl-feedback-providers-question-answer-slider',
  templateUrl: './question-answer-slider.component.html',
  styleUrls: ['./question-answer-slider.component.scss'],
})
export class QuestionAnswerSliderComponent implements OnChanges {
  @Input() question: IncludedFeedbackQuestion;
  @Input() feedbackRecipient: FeedbackRecipient;
  @Input() answers: FeedbackEnrichedAnswer[];
  @Input() language: string;
  @Input() organization: Organization;

  currentAnswer?: FeedbackEnrichedAnswer;
  title = '';
  min = 0;
  max = 10;
  ticks: number[] = [];
  markers: Marker[] = [];
  chartData: SimpleChartData[] = [];
  buckets: BucketModel[] = [];
  leftLabel = '';
  rightLabel = '';
  feedbackProviderResponsesCount: number;

  ngOnChanges(_changes: SimpleChanges): void {
    this.updateView();
  }

  private updateView(): void {
    if (!this.question) return;

    this.setTitle();
    this.currentAnswer = this.findAnswer();
    if (!this.currentAnswer) return;
    this.feedbackProviderResponsesCount = this.getFeedbackProviderResponsesCount();

    this.setRange();
    const range = this.max - this.min;

    this.leftLabel = this.getOptionLabelByDirection('LEFT');
    this.rightLabel = this.getOptionLabelByDirection('RIGHT');

    const providerMarkers = this.buildProviderMarkers(range);
    this.addSelfMarker(range, providerMarkers);
    this.buckets = this.createBuckets(providerMarkers, range);
    this.ticks = this.buildTicks(range);
    this.chartData = this.buildChartDataFromBuckets(this.buckets);
  }

  private getFeedbackProviderResponsesCount(): number {
    return this.currentAnswer?.sliderScores?.length ?? 0;
  }

  private setTitle(): void {
    this.title =
      LanguageCodeHelper.getDataByUserLanguageCode(this.question.question.instructions, this.organization, this.language)?.value ?? '';
  }

  private getOptionLabelByDirection(direction: 'LEFT' | 'RIGHT'): string {
    const option = this.question.question.options?.find((o) => o.direction === direction);
    if (!option) return '';
    return (
      LanguageCodeHelper.getDataByUserLanguageCode(option.values ?? [], this.organization, this.language)?.value ??
      option.values?.[0]?.value ??
      ''
    );
  }

  private addSelfMarker(range: number, list: Marker[]): void {
    const learnerScore = this.currentAnswer?.learnerQuestionAnswer?.selectedOptions?.[0]?.userScore;
    if (!this.feedbackRecipient) return;
    if (typeof learnerScore !== 'number' || !Number.isFinite(learnerScore)) return;
    if (learnerScore < this.min || learnerScore > this.max) return;

    const uid = this.feedbackRecipient.uid;
    const label = (this.feedbackRecipient.firstName ?? '').trim() || 'Self rating';
    const image = this.feedbackRecipient.imageUrl;
    const leftPercent = this.clampPercent(((learnerScore - this.min) / range) * 100);

    const exists = list.some(
      (marker) => (marker.uid && marker.uid === uid) || (marker.label === label && Math.abs(marker.score - learnerScore) < 1e-6),
    );
    if (!exists) list.push({ uid, label, image, score: learnerScore, leftPercent });
  }

  private createBuckets(list: Marker[], range: number): BucketModel[] {
    const bucketMap = new Map<number, Marker[]>();

    for (const marker of list) {
      const bucketValue = Math.round(marker.score);
      if (marker.score < this.min || marker.score > this.max) continue;
      marker.leftPercent = this.clampPercent(((bucketValue - this.min) / range) * 100);
      const existing = bucketMap.get(bucketValue) ?? [];
      existing.push(marker);
      bucketMap.set(bucketValue, existing);
    }

    return Array.from(bucketMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([bucketValue, markers]) => {
        const count = markers.length;
        const { sizeClass, visibleCount } = this.getBucketSizing(count);
        return {
          value: bucketValue,
          leftPercent: markers[0].leftPercent,
          markers,
          sizeClass,
          visibleCount,
        };
      });
  }

  private getBucketSizing(count: number): { sizeClass: BucketModel['sizeClass']; visibleCount: number } {
    if (count <= 2) return { sizeClass: 'large', visibleCount: 2 };
    if (count <= 4) return { sizeClass: 'medium', visibleCount: 3 };
    return { sizeClass: 'small', visibleCount: 4 };
  }

  private buildChartDataFromBuckets(buckets: BucketModel[]) {
    return buckets.map((bucket) => ({
      label: String(bucket.value),
      score: bucket.markers.length,
      selectedCount: bucket.markers.length,
      allResponsesCount: this.currentAnswer.numberOfFeedbackResponses,
    }));
  }

  private findAnswer(): FeedbackEnrichedAnswer | undefined {
    return this.answers?.find((a) => a.questionUid === this.question.question.uid);
  }

  private setRange(): void {
    this.min = this.question.question.min ?? 0;
    this.max = this.question.question.max ?? 10;
    if (this.max === this.min) this.max = this.min + 1;
  }

  private buildProviderMarkers(range: number): Marker[] {
    return (this.currentAnswer.sliderScores ?? [])
      .map((s) => ({
        uid: undefined as string | undefined,
        label: s.providerName ?? 'User',
        image: s.providerImage,
        score: Number(s.score),
        leftPercent: 0,
      }))
      .filter((marker) => Number.isFinite(marker.score) && marker.score >= this.min && marker.score <= this.max)
      .map((marker) => ({ ...marker, leftPercent: this.clampPercent(((marker.score - this.min) / range) * 100) }));
  }

  private buildTicks(range: number): number[] {
    const count = Math.floor(range) + 1;
    return Array.from({ length: count }, (_, i) => this.min + i);
  }

  private clampPercent(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }
}
